import React, { useEffect, useState, useRef } from 'react';
import SecretariatShell from './SecretariatShell';
import { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Loader2, Upload, CheckCircle2, FileSpreadsheet } from 'lucide-react';

const STATUT_CLS = {
  reconnu: 'text-emerald-300',
  nouveau: 'text-sky-300',
  doublon: 'text-orange-300',
  manquant: 'text-red-300',
  mise_a_jour: 'text-emerald-300',
};
const STATUT_LABEL = { reconnu: 'Reconnu ✓', nouveau: 'Nouvel élève (sera créé)', doublon: 'Doublon (ignoré)', manquant: 'Élève manquant', mise_a_jour: 'Fiche mise à jour ✓' };

export default function SecretariatImportPage() {
  const { user, loading: authLoading } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [teacherId, setTeacherId] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('lecons');
  const inputRef = useRef(null);

  const importCall = (f, dry) => (mode === 'eleves' ? api.secImportEleves(f, teacherId, dry) : api.secImportPlanning(f, teacherId, dry));

  useEffect(() => {
    if (authLoading || !user) return;
    api.secTeachers().then((d) => {
      setTeachers(d.items || []);
      if (d.items?.length) setTeacherId(d.items[0].id);
    }).catch(() => {});
  }, [authLoading, user]);

  if (authLoading) return <SecretariatShell><div className="p-12 text-white/50">Chargement…</div></SecretariatShell>;
  if (!user || (user.role !== 'secretaire' && !user.is_admin && !user.secretariat_access)) return <SecretariatShell><div className="p-12 text-center text-white/60">Accès réservé au secrétariat.</div></SecretariatShell>;

  const analyse = async (f) => {
    setError(''); setResult(null); setPreview(null); setBusy(true);
    try {
      const r = await importCall(f, true);
      setPreview(r);
    } catch (e) { setError(e?.response?.data?.detail || 'Analyse impossible — vérifie le fichier.'); }
    setBusy(false);
  };

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    analyse(f);
  };

  const doImport = async () => {
    setBusy(true); setError('');
    try {
      const r = await importCall(file, false);
      setResult(r); setPreview(null); setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (e) { setError(e?.response?.data?.detail || 'Import impossible.'); }
    setBusy(false);
  };

  return (
    <SecretariatShell>
      <section className="max-w-5xl mx-auto px-4 lg:px-6 mt-8" data-testid="secretariat-import">
        <SectionTitle kicker="Secrétariat · Synchronisation" title="Import planning (Codes Rousseau)" sub="Importe un fichier CSV ou Excel exporté de ton outil actuel : les leçons, et les élèves inconnus, sont créés automatiquement. Les doublons sont ignorés." />

        {/* Fichiers modèles pour petite structure */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5 mb-6" data-testid="import-templates">
          <div className="text-white font-semibold text-[14px]">📄 Pas d&apos;outil type Codes Rousseau ? Utilisez nos fichiers modèles</div>
          <div className="text-white/55 text-[12.5px] mt-1 leading-relaxed">Remplissez-les dans Excel (ou LibreOffice), enregistrez, puis importez-les ci-dessous. Format simple, pensé pour les petites structures.</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href="/modeles/modele_planning_codevoiture.csv" download data-testid="template-planning" className="h-9 px-4 rounded-full border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-[12.5px] font-bold inline-flex items-center gap-1.5">⬇ Modèle planning (.csv)</a>
            <a href="/modeles/modele_eleves_codevoiture.csv" download data-testid="template-eleves" className="h-9 px-4 rounded-full border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-[12.5px] font-bold inline-flex items-center gap-1.5">⬇ Modèle élèves (.csv)</a>
          </div>
          <div className="text-white/40 text-[11.5px] mt-2">Types reconnus : conduite, code, examen, rendez-vous pédagogique, plateau moto. Dates au format AAAA-MM-JJ, heures HH:MM.</div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-6 mb-5">
          <div className="flex gap-2 mb-5" data-testid="import-mode-switch">
            {[['lecons', '📅 Leçons (planning)'], ['eleves', '👤 Fiches élèves']].map(([v, l]) => (
              <button key={v} onClick={() => { setMode(v); setPreview(null); setResult(null); setFile(null); setError(''); if (inputRef.current) inputRef.current.value = ''; }} data-testid={`import-mode-${v}`}
                className={`h-10 px-5 rounded-full border text-[13px] font-bold ${mode === v ? 'bg-amber-500 text-black border-amber-500' : 'text-white/60 border-white/15 hover:border-white/40'}`}>{l}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <label className="block text-[12px] text-white/55">{mode === 'eleves' ? 'Enseignant référent (optionnel)' : 'Enseignant à qui affecter les leçons'}
              <select value={teacherId} onChange={(e) => setTeacherId(e.target.value)} data-testid="import-teacher-select" className="mt-1 w-full h-11 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]">
                {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <div className="text-[11.5px] text-white/40 self-end pb-1">
              Colonnes attendues : {mode === 'eleves'
                ? <code className="text-amber-300">nom · prenom · formation · heures_realisees · heures_contrat</code>
                : <code className="text-amber-300">date · heure_debut · heure_fin · type · eleve</code>}<br />
              (ordre libre, séparateur ; ou , — formats .csv / .xlsx)
            </div>
          </div>
          <label className="block rounded-2xl border-2 border-dashed border-white/15 hover:border-amber-500/50 transition p-8 text-center cursor-pointer" data-testid="import-dropzone">
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="import-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
            <FileSpreadsheet size={28} className="mx-auto text-amber-400/70 mb-2" />
            <div className="text-white text-[14px] font-semibold">{file ? file.name : 'Choisir le fichier planning'}</div>
            <div className="text-white/40 text-[12px] mt-1">CSV ou Excel · 2 Mo max</div>
          </label>
          {busy && <div className="mt-4 flex items-center gap-2 text-white/60 text-[13px]"><Loader2 size={14} className="animate-spin" /> Analyse en cours…</div>}
          {error && <div className="mt-4 rounded-xl bg-red-950/30 border border-red-700/40 p-3 text-red-300 text-[12.5px]" data-testid="import-error">⚠️ {String(error)}</div>}
        </div>

        {preview && (
          <div className="rounded-2xl border border-amber-700/30 bg-amber-950/[0.06] p-6 mb-5" data-testid="import-preview">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4">
              <h2 className="text-white text-[15px] font-semibold">Aperçu avant import</h2>
              <span className="text-white/60 text-[12.5px]">{preview.total_rows} {mode === 'eleves' ? 'élèves détectés' : 'leçons détectées'}</span>
              <span className="text-sky-300 text-[12.5px]">{preview.new_students} nouveaux élèves</span>
              {mode === 'eleves'
                ? <span className="text-emerald-300 text-[12.5px]">{preview.to_update} fiches mises à jour</span>
                : <span className="text-orange-300 text-[12.5px]">{preview.skipped_duplicates} doublons ignorés</span>}
              <button onClick={doImport} disabled={busy} data-testid="import-confirm-btn" className="ml-auto h-11 px-6 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black text-[13.5px] font-bold inline-flex items-center gap-2">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Importer maintenant
              </button>
            </div>
            {preview.errors?.length > 0 && (
              <div className="mb-3 rounded-xl bg-red-950/25 border border-red-700/30 p-3 text-red-300 text-[12px]">{preview.errors.map((e, i) => <div key={i}>⚠ {e}</div>)}</div>
            )}
            <div className="rounded-xl border border-white/10 overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full min-w-[560px] text-[12.5px]">
                <thead className="bg-white/[0.03] text-white/45 text-[10.5px] uppercase tracking-wider sticky top-0">
                  {mode === 'eleves'
                    ? <tr><th className="text-left p-2.5">Élève</th><th className="text-left p-2.5">Formation</th><th className="text-left p-2.5">Heures faites</th><th className="text-left p-2.5">Forfait</th><th className="text-left p-2.5">Statut</th></tr>
                    : <tr><th className="text-left p-2.5">Date</th><th className="text-left p-2.5">Heure</th><th className="text-left p-2.5">Durée</th><th className="text-left p-2.5">Type</th><th className="text-left p-2.5">Élève</th><th className="text-left p-2.5">Statut</th></tr>}
                </thead>
                <tbody className="divide-y divide-white/5">
                  {preview.preview.map((p) => (
                    mode === 'eleves' ? (
                      <tr key={p.ligne}>
                        <td className="p-2.5 text-white font-medium">{p.eleve}</td>
                        <td className="p-2.5 text-white/60">{p.formation}</td>
                        <td className="p-2.5 font-mono-spec text-white/75">{p.heures_realisees} h</td>
                        <td className="p-2.5 font-mono-spec text-white/75">{p.forfait != null ? `${p.forfait} h` : '—'}</td>
                        <td className={`p-2.5 font-semibold ${STATUT_CLS[p.eleve_statut] || 'text-white/50'}`}>{STATUT_LABEL[p.eleve_statut] || p.eleve_statut}</td>
                      </tr>
                    ) : (
                    <tr key={p.ligne}>
                      <td className="p-2.5 font-mono-spec text-white/75">{p.date}</td>
                      <td className="p-2.5 font-mono-spec text-white/75">{p.heure}</td>
                      <td className="p-2.5 text-white/60">{p.duree} min</td>
                      <td className="p-2.5 text-white/60">{p.type_source || p.type}</td>
                      <td className="p-2.5 text-white font-medium">{p.eleve}</td>
                      <td className={`p-2.5 font-semibold ${STATUT_CLS[p.eleve_statut] || 'text-white/50'}`}>{STATUT_LABEL[p.eleve_statut] || p.eleve_statut}</td>
                    </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result && (
          <div className="rounded-2xl border border-emerald-700/40 bg-emerald-950/15 p-6" data-testid="import-result">
            <div className="flex items-center gap-2 mb-2"><CheckCircle2 size={18} className="text-emerald-400" /><h2 className="text-white text-[16px] font-semibold">Import terminé !</h2></div>
            <ul className="text-white/75 text-[13.5px] space-y-1">
              {mode === 'eleves' ? (
                <>
                  <li>✅ <b>{result.created}</b> nouvelles fiches élèves créées</li>
                  <li>🔄 <b>{result.updated}</b> fiches existantes mises à jour (formule, heures faites, forfait)</li>
                  <li>💡 Pense à compléter les <b>emails</b> pour relier leurs comptes élèves</li>
                </>
              ) : (
                <>
                  <li>✅ <b>{result.created}</b> leçons créées au planning</li>
                  <li>👤 <b>{result.students_created}</b> nouvelles fiches élèves créées (pense à compléter leurs emails pour relier leurs comptes)</li>
                  <li>↩️ <b>{result.skipped_duplicates}</b> doublons ignorés</li>
                </>
              )}
              {result.errors?.length > 0 && <li className="text-orange-300">⚠ {result.errors.length} lignes ignorées ({result.errors[0]}…)</li>}
            </ul>
            <a href={mode === 'eleves' ? '/secretariat/eleves' : '/secretariat/planning'} className="inline-flex mt-4 h-10 px-5 rounded-full bg-white text-black text-[13px] font-bold items-center">{mode === 'eleves' ? 'Voir les élèves →' : 'Voir le planning →'}</a>
          </div>
        )}
      </section>
    </SecretariatShell>
  );
}
